/** @jsx jsx */
import { useEffect, useState } from 'react'
import { Box, Container, Grid, Heading, jsx } from 'theme-ui'
import MarkerIcon from '~/assets/images/icons/marker.inline.svg'
import SearchIcon from '~/assets/images/icons/search.inline.svg'
import StoreLocator from '~/components/StoreLocator'
import useLocationsListQuery from '~/hooks/graphql/queries/use-locations-list'
import ipbaseApi from '~/services/ipbase/client'

const StoreLocatorSection = ({ cta, description, footer, title }) => {
  const retailLocations = useLocationsListQuery()
  const [defaultCenter, setDefaultCenter] = useState(null)

  const storeLocatorUi = {
    list: {
      marker: {
        icon: (
          <Box
            sx={{
              height: '40px',
              width: '32px',
            }}
          >
            <MarkerIcon />
          </Box>
        ),
      },
    },
    map: {
      marker: {
        icon: {
          fillColor: '#193F61',
        },
        iconActive: {
          fillColor: '#2A679E',
        },
      },
    },
    search: {
      input: {
        placeholder: 'Enter Zip or Address',
      },
      button: {
        label: (
          <Box
            sx={{
              fill: 'white',
              height: '20px',
              width: '20px',
            }}
          >
            <SearchIcon />
          </Box>
        ),
      },
    },
    title: (
      <Heading as="h2" mb={1} variant="title4">
        {'Maker Locations'}
      </Heading>
    ),
  }

  const storeLocatorSx = {
    layout: {
      wrapper: {
        height: ['732px', '894px', '480px'],
      },
      grid: {
        mx: [-2, 0],
      },
      search: {
        px: [2, 0],
      },
    },
    list: {
      item: {
        address: {
          variant: 'text.body3',
        },
        distance: {
          variant: 'text.description',
          color: 'greyDark',
          position: 'relative',
          top: ['2px', null, '3px'],
        },
        marker: {
          pt: '4px',
        },
        title: {
          variant: 'text.body2',
          color: 'brandPrimary',
          lineHeight: '24px',
        },
      },
    },
    map: {
      map: {
        '.store-locator-map-tooltip': {
          variant: 'text.body3',
        },
      },
    },
    error: {
      variant: 'text.body3',
      color: 'error',
      mb: 2,
    },
    search: {
      button: {
        px: 2,
      },
      input: {
        variant: 'text.body3',
        backgroundColor: 'white',
        borderColor: 'greyMedium',
        borderRadius: 0,
      },
    },
  }

  useEffect(() => {
    const initCenter = async () => {
      // @todo Use checkGeolocation() and/or store coordinates in Redux?
      const userLocation = await ipbaseApi.getLocation()

      setDefaultCenter({
        lat: userLocation?.latitude || 37.7790262,
        lng: userLocation?.longitude || -122.419906,
      })
    }

    initCenter()
  }, [])

  return (
    <Box pt={[7, null, 9]} pb={[8, null, 10]}>
      <Container>
        <Grid columns={[1, 12]} mb={[5, null, 6]} sx={{ textAlign: 'center' }}>
          <Box sx={{ gridColumn: [null, '2 / 12', '3 / 11', '4 / 10'] }}>
            <Heading as="h1" variant="title1">
              {title}
            </Heading>
            {description && (
              <Box
                mt={2}
                variant="text.body1"
                sx={{
                  p: {
                    m: 0,
                  },
                  a: {
                    variant: 'text.link',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: description.childMarkdownRemark.html,
                }}
              />
            )}
          </Box>
        </Grid>
        <StoreLocator
          defaultCenter={defaultCenter}
          defaultLocations={retailLocations}
          mapId="bd82bc5179ff34c"
          styles={storeLocatorSx}
          ui={storeLocatorUi}
        />
        {footer && (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              mt={[5, null, 8]}
              variant="text.body1"
              sx={{
                p: {
                  m: 0,
                },
              }}
              dangerouslySetInnerHTML={{
                __html: footer.childMarkdownRemark.html,
              }}
            />
            {cta && <Box mt={[3, 4]}>{cta}</Box>}
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default StoreLocatorSection
